import { useState, useCallback } from 'react';
import { PageData } from '../types';
import { clonePageWithMapping, generateId } from '../utils/layerUtils';

export const usePageManager = (initialPage?: PageData) => {
    const [pages, setPages] = useState<PageData[]>(initialPage ? [initialPage] : []);
    const [currentPageIndex, setCurrentPageIndex] = useState(0);

    const addPage = useCallback((defaultSettings: any) => {
        const nextIndex = pages.length;
        const newPage: PageData = {
            id: generateId('page'),
            name: `Page ${nextIndex + 1}`,
            dynamicLayers: [],
            layerPositions: {},
            textContents: {},
            textStyles: {},
            backgroundSettings: {},
            imageSettings: {},
            shapeSettings: {},
            scrollerSettings: {},
            widgetSettings: {},
            videoSettings: {},
            templateSettings: defaultSettings,
        };
        setPages(prev => [...prev, newPage]);
        return nextIndex;
    }, [pages.length]);

    const duplicatePage = useCallback((index: number, latestPageData?: PageData) => {
        const pageToDuplicate = latestPageData || pages[index];
        if (!pageToDuplicate) return undefined;

        // Properly clone with ID remapping
        const newPage = clonePageWithMapping(pageToDuplicate);

        const nextIndex = index + 1;
        setPages(prev => {
            const updated = [...prev];
            updated.splice(nextIndex, 0, newPage);
            return updated;
        });

        return nextIndex;
    }, [pages]);

    const switchPage = useCallback((newIndex: number) => {
        if (newIndex < 0 || newIndex >= pages.length) return false;
        setCurrentPageIndex(newIndex);
        return true;
    }, [pages.length]);

    const removePage = useCallback((index: number) => {
        setPages(prev => {
            if (prev.length <= 1) return prev; // Guard against deleting last page
            return prev.filter((_, i) => i !== index);
        });

        setCurrentPageIndex(prev => {
            if (index < prev) return prev - 1;
            if (index === prev) return Math.max(0, prev - 1);
            return prev;
        });

        return true;
    }, []);

    return {
        pages,
        setPages,
        currentPageIndex,
        setCurrentPageIndex,
        addPage,
        duplicatePage,
        switchPage,
        removePage
    };
};
